import { useEffect } from 'react';

import { usePageNativeEmbedClient } from '@confluence/embedded-confluence-allowed-features/entry-points/usePageNativeEmbedClient.ts';
import { useCanPerformContentOperation } from '@confluence/entity-operations/entry-points/entityOperationsHelpers.ts';
import type { TargetContentType } from '@confluence/entity-operations/entry-points/operationsTypes.ts';
import { OperationTypes } from '@confluence/entity-operations/entry-points/operationsTypes.ts';
import { useContentType } from '@confluence/page-context';
import { CURRENT_STATUS } from '@confluence/page-utils/entry-points/enums.ts';

export const useSyncEditButtonPermissionWithHost = ({
	contentId,
	enabled = true,
	phase2Enabled = false,
}: {
	contentId: string;
	enabled?: boolean;
	phase2Enabled?: boolean;
}): void => {
	const { sendChildCommand } = usePageNativeEmbedClient() ?? {};
	const [contentType] = useContentType();

	// When phase2Enabled is true, do NOT pass contentType to
	// useCanPerformContentOperation. The GraphQL query fetches operations by
	// contentId only. When contentType is omitted, canPerformContentOperation
	// falls back to the API-returned type (e.g. 'blogpost' for blogs), which
	// correctly matches the operations list.
	// Previously, useContentType() was used with a ?? 'page' fallback, but
	// PageStore.contentType is undefined on initial render, causing the check
	// to use 'page' for blogs — which never matches targetType 'blogpost',
	// incorrectly returning false and hiding the edit button.
	const canUpdateContent = useCanPerformContentOperation({
		contentId,
		...(phase2Enabled ? {} : { contentType: (contentType ?? 'page') as TargetContentType }),
		operation: OperationTypes.UPDATE,
		status: CURRENT_STATUS,
	});

	useEffect(() => {
		if (!enabled || canUpdateContent === undefined || !sendChildCommand) {
			return;
		}
		sendChildCommand({
			type: 'set-show-edit-button',
			show: canUpdateContent ? null : false,
			// `set-show-edit-button` is a host-side native-embeds extension
			// that is not yet typed in the public child-command union.
		} as any);
	}, [canUpdateContent, enabled, sendChildCommand]);
};
